import { useState, useRef, useEffect, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { chatHeaders } from "./supabaseClient";
import { linkify } from "./linkify";
import { CookieSettingsButton } from "./CookieConsent";
import Account from "./Account";
import Upgrade from "./Upgrade";
import ConversationSidebar from "./ConversationSidebar";
import { useConversations } from "./useConversations";
import FullPageChat from "./FullPageChat";
import PrivacyPolicy from "./PrivacyPolicy";
import TermsOfService from "./TermsOfService";
import About from "./About";
import ComparePlans from "./ComparePlans";
import BlogIndex from "./BlogIndex";
import BlogPost from "./BlogPost";

/* ─────────────────────── GLOBAL STYLES ─────────────────────── */
const GlobalStyles = () => (
  <style>{`
    :root {
      --ink: #1a1a1a;
      --ink-soft: #4a4a4a;
      --ink-muted: #6e6e6e;
      --ink-faint: #6e6e6e;
      --surface: #fafaf8;
      --surface-warm: #f5f3ef;
      --surface-mid: #edeae4;
      --border: #e0ddd6;
      --border-light: #eceae5;
      --accent: #2d5a3d;
      --accent-light: #3a7350;
      --accent-mist: #e8f0eb;
      --warm: #c97c4a;
    }

    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    html { scroll-behavior: smooth; overflow-x: hidden; }

    body {
      background: var(--surface);
      color: var(--ink);
      font-family: 'Karla', sans-serif;
      font-weight: 400;
      font-size: 16px;
      line-height: 1.7;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      overflow-x: hidden;
    }

    ::selection { background: var(--accent-mist); color: var(--accent); }

    @keyframes pulse {
      0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
      40% { opacity: 1; transform: scale(1); }
    }

    .reveal {
      opacity: 0;
      transform: translateY(32px);
      transition: opacity 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94),
                  transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    }
    .reveal.visible {
      opacity: 1;
      transform: translateY(0);
    }

    .nav-links { display: flex; gap: 32px; align-items: center; }
    @media (max-width: 680px) { .nav-links { display: none; } }

    .hamburger-btn {
      display: none;
      background: none; border: none; cursor: pointer;
      padding: 6px; border-radius: 6px;
      color: var(--ink-soft);
      align-items: center; justify-content: center;
      transition: all 0.15s; line-height: 0;
    }
    .hamburger-btn:hover { background: var(--surface-warm); color: var(--ink); }
    @media (max-width: 680px) { .hamburger-btn { display: flex; } }

    .mobile-nav {
      border-top: 1px solid var(--border-light);
      padding: 4px 0 12px;
      display: flex; flex-direction: column;
    }
    @media (min-width: 681px) { .mobile-nav { display: none !important; } }

    .mobile-nav-link {
      background: none; border: none; border-bottom: 1px solid var(--border-light);
      cursor: pointer;
      font-family: 'Karla', sans-serif; font-size: 15px; font-weight: 400;
      color: var(--ink-soft); text-align: left;
      padding: 14px 0; width: 100%; transition: color 0.15s;
    }
    .mobile-nav-link:last-child { border-bottom: none; }
    .mobile-nav-link:hover { color: var(--ink); }
    .mobile-nav-link:focus-visible { color: var(--ink); }

    .prose { font-size: 16px; line-height: 1.85; color: var(--ink-soft); }
    .prose h2 {
      font-family: 'DM Serif Display', serif; font-size: 22px; font-weight: 400;
      color: var(--ink); letter-spacing: -0.01em;
      margin: 36px 0 12px; line-height: 1.3;
    }
    .prose p { margin-bottom: 20px; }
    .prose ul { margin: 0 0 20px 24px; }
    .prose li { margin-bottom: 10px; line-height: 1.75; }
    .prose strong { color: var(--ink); font-weight: 600; }

    @media (max-width: 680px) {
      .hide-on-mobile { display: none !important; }
      .nav-header { padding: 12px 16px !important; }
      .nav-right-group { gap: 10px !important; }
      .mobile-nav { padding: 6px 0 16px; }
      .chat-header { padding: 10px 12px !important; }
      .chat-header-left { gap: 8px !important; }
      .chat-header-right { gap: 8px !important; }
      .new-btn { padding: 5px 8px !important; }
    }

    /* Hero responsive */
    .hero-split {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 48px;
      align-items: center;
      max-width: 1200px;
      margin: 0 auto;
      padding: clamp(60px, 10vh, 120px) 24px;
    }
    .hero-text { position: relative; z-index: 2; }
    .hero-image-wrap {
      position: relative;
      border-radius: 12px;
      overflow: hidden;
      aspect-ratio: 4/3;
    }
    .hero-image-wrap img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
    @media (max-width: 840px) {
      .hero-split {
        grid-template-columns: 1fr;
        gap: 0;
        padding: 0;
        position: relative;
        min-height: 0;
        height: auto;
      }
      .hero-text {
        position: relative;
        z-index: 2;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 100px 24px 40px;
        min-height: 85svh;
      }
      .hero-text h1 {
        font-size: clamp(28px, 7vw, 40px) !important;
        margin-bottom: 16px !important;
      }
      .hero-text .hero-sub {
        font-size: 15px !important;
        margin-bottom: 10px !important;
      }
      .hero-text .hero-italic {
        font-size: 13px !important;
        margin-bottom: 28px !important;
      }
      .hero-image-wrap {
        position: absolute;
        inset: 0;
        border-radius: 0;
        aspect-ratio: auto;
        height: 100%;
        width: 100%;
      }
      .hero-image-wrap img {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: 35% center;
      }
      .hero-overlay {
        position: absolute;
        inset: 0;
        background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.45) 60%, rgba(0,0,0,0.1) 100%);
        z-index: 1;
      }
      .hero-text h1,
      .hero-text p,
      .hero-text .hero-label { color: #fff !important; }
      .hero-text .hero-sub { color: rgba(255,255,255,0.9) !important; }
      .hero-text .hero-italic { color: rgba(255,255,255,0.85) !important; }
      .hero-text button { background-color: #fff !important; color: var(--ink) !important; }
    }
    @media (min-width: 841px) {
      .hero-overlay { display: none; }
    }

    /* iMessage-style chat */
    .imessage-bubble-irene {
      background: var(--surface-warm);
      border: 1px solid var(--border-light);
      border-radius: 18px 18px 18px 6px;
      color: var(--ink);
    }
    .imessage-bubble-user {
      background: var(--accent);
      border-radius: 18px 18px 6px 18px;
      color: #fff;
    }

    /* WCAG 2.4.11: focus visible with 3px outline at ≥3:1 contrast */
    *:focus-visible {
      outline: 3px solid var(--accent);
      outline-offset: 2px;
    }

    /* Screen-reader-only utility — clip-rect pattern, NOT display:none */
    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    /* Skip link for ADA */
    .skip-link {
      position: absolute;
      top: -100px;
      left: 16px;
      background: var(--accent);
      color: #fff;
      padding: 8px 16px;
      border-radius: 4px;
      font-size: 14px;
      z-index: 1000;
      transition: top 0.2s;
    }
    .skip-link:focus { top: 16px; }

    /* Sidebar toggle */
    .sidebar-toggle {
      background: none;
      border: 1px solid var(--border);
      border-radius: 6px;
      padding: 6px 10px;
      cursor: pointer;
      font-family: inherit;
      font-size: 13px;
      color: var(--ink-soft);
      transition: all 0.2s;
      display: flex;
      align-items: center;
      gap: 6px;
      white-space: nowrap;
    }
    .sidebar-toggle:hover {
      border-color: var(--ink-muted);
      color: var(--ink);
    }
    @media (max-width: 480px) {
      .sidebar-toggle span.sidebar-label { display: none; }
    }
  `}</style>
);

/* ─────────────────────── SCROLL REVEAL HOOK ─────────────────────── */
const useReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.unobserve(el); } },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, visible];
};

const Reveal = ({ children, delay = 0, style = {} }) => {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={`reveal ${visible ? "visible" : ""}`}
      style={{ transitionDelay: `${delay}s`, ...style }}
    >
      {children}
    </div>
  );
};

/* ─────────────────────── COMPONENTS ─────────────────────── */

const ThinkingDots = () => (
  <div style={{ display: "flex", alignItems: "center", gap: "5px", padding: "8px 0" }}
    aria-hidden="true">
    {[0, 1, 2].map((i) => (
      <div key={i} style={{
        width: "7px", height: "7px", borderRadius: "50%",
        backgroundColor: "var(--ink-muted)",
        animation: `pulse 1.4s ease-in-out ${i * 0.2}s infinite`,
      }} />
    ))}
  </div>
);

/* ─────────────────────── NAV ─────────────────────── */
const navLinkStyle = {
  fontSize: "14px", fontWeight: "400", color: "var(--ink-soft)",
  cursor: "pointer", background: "none", border: "none",
  padding: "4px 0", fontFamily: "inherit", transition: "color 0.2s",
};

const HamburgerIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
    <line x1="3" y1="6" x2="17" y2="6"/><line x1="3" y1="10" x2="17" y2="10"/><line x1="3" y1="14" x2="17" y2="14"/>
  </svg>
);
const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
    <line x1="5" y1="5" x2="15" y2="15"/><line x1="15" y1="5" x2="5" y2="15"/>
  </svg>
);

const Nav = ({ onScrollTo, user, isPaid, onSignIn, onAccount, onAbout, onCompare, onBlog, onBackToChat, onHome }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const close = () => setMobileOpen(false);
  return (
    <header className="nav-header" style={{
      position: "sticky", top: 0, zIndex: 100,
      padding: "18px 24px",
      backgroundColor: "rgba(250, 250, 248, 0.92)",
      backdropFilter: "blur(16px)",
      borderBottom: "1px solid var(--border-light)",
    }}>
      <nav style={{
        maxWidth: "1200px", margin: "0 auto",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }} aria-label="Main navigation">
        {onHome ? (
          <button
            onClick={onHome}
            aria-label="Ask Irene home"
            style={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: "20px", color: "var(--ink)", letterSpacing: "-0.02em",
              background: "none", border: "none", cursor: "pointer", padding: 0,
              whiteSpace: "nowrap",
            }}
          >Ask Irene</button>
        ) : (
          <a href="/" style={{ textDecoration: "none" }} aria-label="Ask Irene home">
            <span style={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: "20px", color: "var(--ink)", letterSpacing: "-0.02em",
              whiteSpace: "nowrap",
            }}>Ask Irene</span>
          </a>
        )}
        <div className="nav-right-group" style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div className="nav-links" role="navigation">
            {!user ? (
              /* Logged-out: scroll links + Pricing + Our story */
              <>
                {[["Talk to Irene", "advisor"], ["How it works", "how-it-works"]].map(([label, id]) => (
                  <button key={id} onClick={() => onScrollTo(id)} style={navLinkStyle}
                    onMouseEnter={(e) => e.target.style.color = "var(--ink)"}
                    onMouseLeave={(e) => e.target.style.color = "var(--ink-soft)"}
                  >{label}</button>
                ))}
                <button onClick={onCompare} style={navLinkStyle}
                  onMouseEnter={(e) => e.target.style.color = "var(--ink)"}
                  onMouseLeave={(e) => e.target.style.color = "var(--ink-soft)"}
                >Pricing</button>
                <button onClick={onAbout} style={navLinkStyle}
                  onMouseEnter={(e) => e.target.style.color = "var(--ink)"}
                  onMouseLeave={(e) => e.target.style.color = "var(--ink-soft)"}
                >Our story</button>
                <button onClick={onBlog} style={navLinkStyle}
                  onMouseEnter={(e) => e.target.style.color = "var(--ink)"}
                  onMouseLeave={(e) => e.target.style.color = "var(--ink-soft)"}
                >Blog</button>
              </>
            ) : (
              /* Logged-in (free or paid): Pricing + Our story + Blog */
              <>
                <button onClick={onCompare} style={navLinkStyle}
                  onMouseEnter={(e) => e.target.style.color = "var(--ink)"}
                  onMouseLeave={(e) => e.target.style.color = "var(--ink-soft)"}
                >Pricing</button>
                <button onClick={onAbout} style={navLinkStyle}
                  onMouseEnter={(e) => e.target.style.color = "var(--ink)"}
                  onMouseLeave={(e) => e.target.style.color = "var(--ink-soft)"}
                >Our story</button>
                <button onClick={onBlog} style={navLinkStyle}
                  onMouseEnter={(e) => e.target.style.color = "var(--ink)"}
                  onMouseLeave={(e) => e.target.style.color = "var(--ink-soft)"}
                >Blog</button>
              </>
            )}
          </div>
          {/* Green "Back to chat" button — paid users only, desktop only (mobile uses hamburger) */}
          {onBackToChat && (
            <button
              className="hide-on-mobile"
              onClick={onBackToChat}
              style={{
                padding: "8px 18px", fontSize: "13px", fontWeight: "500",
                backgroundColor: "var(--accent)", color: "#fff",
                border: "none", borderRadius: "6px", cursor: "pointer",
                fontFamily: "inherit", transition: "background-color 0.2s",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "var(--accent-light)"}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "var(--accent)"}
            >Back to chat</button>
          )}
          {user ? (
            <button
              onClick={onAccount}
              style={{
                display: "flex", alignItems: "center", gap: "8px",
                background: "none", border: "1px solid var(--border)",
                borderRadius: "24px", padding: "6px 14px 6px 6px",
                cursor: "pointer", fontFamily: "inherit",
                transition: "border-color 0.2s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = "var(--ink-muted)"}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = "var(--border)"}
              aria-label="Your account"
            >
              {user.user_metadata?.avatar_url ? (
                <img
                  src={user.user_metadata.avatar_url}
                  alt=""
                  style={{ width: "26px", height: "26px", borderRadius: "50%" }}
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div style={{
                  width: "26px", height: "26px", borderRadius: "50%",
                  backgroundColor: "var(--accent)", color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "12px", fontWeight: "600",
                }}>
                  {(user.user_metadata?.full_name || user.email || "U").charAt(0).toUpperCase()}
                </div>
              )}
              <span style={{ fontSize: "13px", color: "var(--ink-soft)" }}>
                {user.user_metadata?.full_name?.split(" ")[0] || "Account"}
              </span>
            </button>
          ) : (
            <button
              onClick={onSignIn}
              style={{
                fontSize: "13px", fontWeight: "500", color: "var(--ink-soft)",
                cursor: "pointer", background: "none",
                border: "1px solid var(--border)",
                borderRadius: "6px", padding: "8px 16px",
                fontFamily: "inherit", transition: "all 0.2s",
              }}
              onMouseEnter={(e) => { e.target.style.borderColor = "var(--ink-muted)"; e.target.style.color = "var(--ink)"; }}
              onMouseLeave={(e) => { e.target.style.borderColor = "var(--border)"; e.target.style.color = "var(--ink-soft)"; }}
            >
              Sign in
            </button>
          )}
          {/* Hamburger — mobile only */}
          <button
            className="hamburger-btn"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <CloseIcon /> : <HamburgerIcon />}
          </button>
        </div>
      </nav>

      {/* Mobile slide-down menu */}
      {mobileOpen && (
        <div className="mobile-nav" style={{ maxWidth: "1200px", margin: "0 auto" }}>
          {!user ? (
            <>
              <button className="mobile-nav-link" onClick={() => { onScrollTo("advisor"); close(); }}>Talk to Irene</button>
              <button className="mobile-nav-link" onClick={() => { onScrollTo("how-it-works"); close(); }}>How it works</button>
              <button className="mobile-nav-link" onClick={() => { onCompare(); close(); }}>Pricing</button>
              <button className="mobile-nav-link" onClick={() => { onAbout(); close(); }}>Our story</button>
              <button className="mobile-nav-link" onClick={() => { onBlog(); close(); }}>Blog</button>
            </>
          ) : (
            <>
              {onBackToChat && (
                <button className="mobile-nav-link" onClick={() => { onBackToChat(); close(); }}
                  style={{ color: "var(--accent)", fontWeight: "500" }}
                >Back to chat</button>
              )}
              <button className="mobile-nav-link" onClick={() => { onCompare(); close(); }}>Pricing</button>
              <button className="mobile-nav-link" onClick={() => { onAbout(); close(); }}>Our story</button>
              <button className="mobile-nav-link" onClick={() => { onBlog(); close(); }}>Blog</button>
            </>
          )}
        </div>
      )}
    </header>
  );
};

/* ─────────────────────── HERO ─────────────────────── */
const Hero = ({ onScrollTo }) => (
  <section aria-labelledby="hero-heading" style={{ position: "relative" }}>
    <div className="hero-split">
      <div className="hero-text">
        <Reveal>
          <p className="hero-label" style={{
            fontSize: "13px", fontWeight: "500", color: "var(--accent)",
            letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "24px",
          }}>Free medical bill advisor</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 id="hero-heading" style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: "clamp(32px, 5vw, 56px)",
            fontWeight: "400", color: "var(--ink)",
            lineHeight: 1.1, letterSpacing: "-0.02em",
            marginBottom: "24px",
          }}>
            You shouldn't need a finance degree to understand your medical bills.
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="hero-sub" style={{
            fontSize: "clamp(16px, 2vw, 19px)", lineHeight: 1.7,
            color: "var(--ink-soft)", maxWidth: "480px",
            marginBottom: "14px",
          }}>
            Ask Irene helps families navigate medical billing, find assistance programs
            you probably qualify for, and understand what you actually owe.
          </p>
        </Reveal>
        <Reveal delay={0.25}>
          <p className="hero-italic" style={{
            fontSize: "15px", color: "var(--ink-muted)",
            fontStyle: "italic", marginBottom: "40px",
          }}>
            Named for a cancer survivor. Built for every family facing medical bills.
          </p>
        </Reveal>
        <Reveal delay={0.35}>
          <button onClick={() => onScrollTo("advisor")} style={{
            padding: "16px 36px", fontSize: "15px", fontWeight: "500",
            backgroundColor: "var(--ink)", color: "var(--surface)",
            border: "none", borderRadius: "6px", cursor: "pointer",
            transition: "all 0.3s ease",
            letterSpacing: "0.01em",
          }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "var(--accent)";
              e.target.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "var(--ink)";
              e.target.style.transform = "translateY(0)";
            }}
          >
            Talk to Irene — it's free
          </button>
        </Reveal>
      </div>
      <div className="hero-image-wrap">
        <div className="hero-overlay" />
        <picture>
          <source srcSet="/hero.webp" type="image/webp" />
          <img
            src="/hero.jpg"
            alt="A family walking together holding hands in warm sunlight"
            loading="eager"
            fetchpriority="high"
            decoding="async"
            width="1200"
            height="900"
          />
        </picture>
      </div>
    </div>
  </section>
);

/* ─────────────────────── DIVIDER ─────────────────────── */
const Divider = () => (
  <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 24px" }}>
    <hr style={{ border: "none", borderTop: "1px solid var(--border)", margin: 0 }} />
  </div>
);

/* ─────────────────────── HOW IT WORKS ─────────────────────── */
const HowItWorks = () => {
  const steps = [
    { num: "01", title: "Tell Irene your situation", desc: "Active treatment, post-treatment, new diagnosis — wherever you are. No judgment. No jargon. Just start talking." },
    { num: "02", title: "She asks the right questions", desc: "Insurance type, what's weighing on you most, what bills you're looking at. Irene listens before she advises." },
    { num: "03", title: "Get your action plan", desc: "Specific steps: what to check, what to dispute, what programs to apply for, and exactly what to say when you call." },
  ];

  return (
    <section id="how-it-works" style={{
      padding: "clamp(64px, 10vh, 120px) 24px",
      maxWidth: "1200px", margin: "0 auto",
    }} aria-labelledby="hiw-heading">
      <Reveal>
        <h2 id="hiw-heading" style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: "clamp(28px, 4vw, 42px)", fontWeight: "400",
          color: "var(--ink)", lineHeight: 1.2,
          letterSpacing: "-0.02em", marginBottom: "64px",
          maxWidth: "480px",
        }}>
          Like having a knowledgeable friend who's been through it.
        </h2>
      </Reveal>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
        gap: "48px 40px",
      }}>
        {steps.map((s, i) => (
          <Reveal key={s.num} delay={i * 0.12}>
            <div>
              <span style={{
                fontSize: "48px", fontFamily: "'DM Serif Display', serif",
                fontWeight: "400", color: "var(--border)",
                lineHeight: 1, display: "block", marginBottom: "16px",
              }}>{s.num}</span>
              <h3 style={{
                fontSize: "18px", fontWeight: "600", color: "var(--ink)",
                marginBottom: "8px",
              }}>{s.title}</h3>
              <p style={{
                fontSize: "15px", lineHeight: 1.7, color: "var(--ink-soft)",
              }}>{s.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

/* ─────────────────────── CAPABILITIES ─────────────────────── */
const Capabilities = () => {
  const items = [
    { title: "EOB & bill reading", desc: "Understand what you're actually being charged — and spot the errors that happen more often than you think." },
    { title: "Negotiation & disputes", desc: "When to push back, what to say, and how to get bills reduced before you pay a dime." },
    { title: "Financial assistance programs", desc: "Most nonprofit hospitals must offer aid under federal law. Most families never apply." },
    { title: "Your legal protections", desc: "The No Surprises Act, medical debt credit rules, and rights you probably don't know you have." },
    { title: "Medication costs", desc: "Pharmaceutical assistance programs, copay cards, and ways to dramatically reduce prescription expenses." },
    { title: "Insurance appeals", desc: "When a claim is denied, you have the right to appeal. Irene walks you through how." },
  ];

  return (
    <section id="capabilities" style={{
      padding: "clamp(64px, 10vh, 120px) 24px",
      maxWidth: "1200px", margin: "0 auto",
    }} aria-labelledby="cap-heading">
      <Reveal>
        <h2 id="cap-heading" style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: "clamp(28px, 4vw, 42px)", fontWeight: "400",
          color: "var(--ink)", lineHeight: 1.2,
          letterSpacing: "-0.02em", marginBottom: "16px",
          maxWidth: "400px",
        }}>
          What Irene helps with.
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <p style={{
          fontSize: "17px", color: "var(--ink-soft)", marginBottom: "56px",
          maxWidth: "520px",
        }}>
          Deep knowledge of medical billing, insurance rules, and patient rights — explained in plain English.
        </p>
      </Reveal>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
        gap: "1px",
        backgroundColor: "var(--border)",
        border: "1px solid var(--border)",
        borderRadius: "8px",
        overflow: "hidden",
      }}>
        {items.map((item, i) => (
          <Reveal key={item.title} delay={i * 0.06} style={{ background: "var(--surface)" }}>
            <div style={{
              padding: "32px 28px",
              backgroundColor: "var(--surface)",
              minHeight: "160px",
              display: "flex", flexDirection: "column", justifyContent: "flex-start",
            }}>
              <h3 style={{
                fontSize: "16px", fontWeight: "600", color: "var(--ink)",
                marginBottom: "8px",
              }}>{item.title}</h3>
              <p style={{
                fontSize: "14px", lineHeight: 1.65, color: "var(--ink-soft)",
              }}>{item.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

/* ─────────────────────── CONTEXT / STATS ─────────────────────── */
const Context = () => (
  <section style={{
    padding: "clamp(64px, 10vh, 100px) 24px",
    backgroundColor: "var(--surface-warm)",
    borderTop: "1px solid var(--border-light)",
    borderBottom: "1px solid var(--border-light)",
  }} aria-labelledby="context-heading">
    <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
      <Reveal>
        <h2 id="context-heading" style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: "clamp(24px, 3.5vw, 36px)", fontWeight: "400",
          color: "var(--ink)", lineHeight: 1.3,
          letterSpacing: "-0.02em", marginBottom: "48px",
          maxWidth: "600px",
        }}>
          Medical debt is the leading cause of bankruptcy in America. It doesn't have to be.
        </h2>
      </Reveal>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "40px",
      }}>
        {[
          { stat: "1 in 4", label: "Americans carry medical debt" },
          { stat: "$88B", label: "Outstanding medical debt nationwide" },
          { stat: "80%", label: "Of medical bills contain errors" },
        ].map((item, i) => (
          <Reveal key={item.stat} delay={i * 0.1}>
            <div>
              <span style={{
                fontFamily: "'DM Serif Display', serif",
                fontSize: "clamp(36px, 5vw, 48px)", fontWeight: "400",
                color: "var(--accent)", display: "block",
                lineHeight: 1, marginBottom: "8px",
              }}>{item.stat}</span>
              <span style={{
                fontSize: "14px", color: "var(--ink-muted)", fontWeight: "400",
              }}>{item.label}</span>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ─────────────────────── CHAT ADVISOR ─────────────────────── */
const IreneAvatar = () => (
  <div style={{
    width: "32px", height: "32px", borderRadius: "50%",
    background: "linear-gradient(135deg, var(--accent), var(--accent-light))",
    display: "flex", alignItems: "center", justifyContent: "center",
    color: "#fff", fontSize: "14px",
    fontFamily: "'DM Serif Display', serif", fontWeight: "400",
    flexShrink: 0,
  }} aria-hidden="true">I</div>
);

const ChatAdvisor = ({
  isPaid,
  user,
  conversationHook,
  onUpgrade,
  sidebarOpen,
  onToggleSidebar,
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [started, setStarted] = useState(false);
  const [showUpgradeHint, setShowUpgradeHint] = useState(false);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);

  const {
    activeConversationId,
    setActiveConversationId,
    createConversation,
    loadMessages,
    saveMessage,
    updateLocalTitle,
  } = conversationHook || {};

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (started && inputRef.current) inputRef.current.focus();
  }, [started]);

  // When a conversation is selected from the sidebar, load its messages
  useEffect(() => {
    if (!activeConversationId || !isPaid) return;
    let cancelled = false;
    (async () => {
      const loaded = await loadMessages(activeConversationId);
      if (!cancelled && loaded.length > 0) {
        setMessages(loaded);
        setStarted(true);
      }
    })();
    return () => { cancelled = true; };
  }, [activeConversationId, isPaid, loadMessages]);

  // Reset chat for a new conversation
  const handleNewConversation = useCallback(() => {
    setMessages([]);
    setStarted(false);
    setInput("");
    if (setActiveConversationId) setActiveConversationId(null);
  }, [setActiveConversationId]);

  // Expose handleNewConversation via ref-like pattern on the hook
  useEffect(() => {
    if (conversationHook) {
      conversationHook._handleNewConversation = handleNewConversation;
    }
  }, [conversationHook, handleNewConversation]);

  const sendMessage = useCallback(async (userMessage, isInitial = false) => {
    const newMessages = isInitial
      ? [] : [...messages, { role: "user", content: userMessage }];
    if (!isInitial) { setMessages(newMessages); setInput(""); }
    setIsLoading(true);

    // For paid users, ensure we have a conversation ID
    let convoId = activeConversationId;
    if (isPaid && user && createConversation) {
      if (!convoId) {
        const convo = await createConversation(user.id);
        if (convo) {
          convoId = convo.id;
        }
      }
      // Save user message (skip for initial auto-message)
      if (!isInitial && convoId) {
        await saveMessage(convoId, "user", userMessage);
        // Update sidebar title from first real user message
        if (updateLocalTitle) updateLocalTitle(convoId, userMessage);
      }
    }

    try {
      const apiMessages = isInitial
        ? [{ role: "user", content: "Hi, I need help with my medical bills." }]
        : newMessages.map((m) => ({ role: m.role, content: m.content }));
      const response = await fetch("/api/chat", {
        method: "POST",
        headers: await chatHeaders(),
        body: JSON.stringify({ messages: apiMessages }),
      });
      const data = await response.json();
      if (!response.ok) {
        const errMsg = data.error || "I'm having a little trouble connecting right now. Please try again in a moment.";
        setMessages((prev) => [...prev, { role: "assistant", content: errMsg }]);
        return;
      }
      const assistantContent = data.response || "I'm sorry, I had trouble responding. Could you try again?";

      setMessages((prev) => [...prev, { role: "assistant", content: assistantContent }]);

      // Save assistant response for paid users
      if (isPaid && convoId && saveMessage) {
        await saveMessage(convoId, "assistant", assistantContent);
      }
    } catch {
      const errorMsg = "I'm having a little trouble connecting right now. Please try again in a moment.";
      setMessages((prev) => [...prev, { role: "assistant", content: errorMsg }]);
    } finally { setIsLoading(false); }
  }, [messages, isPaid, user, activeConversationId, createConversation, saveMessage, updateLocalTitle]);

  const handleStart = () => { setStarted(true); sendMessage("", true); };

  return (
    <section id="advisor" style={{
      padding: "clamp(64px, 10vh, 120px) 24px",
      maxWidth: "1200px", margin: "0 auto",
    }} aria-labelledby="advisor-heading">
      <div style={{ maxWidth: "760px", margin: "0 auto" }}>
        {!started && (
          <Reveal>
            <div style={{ textAlign: "center", marginBottom: "40px" }}>
              <h2 id="advisor-heading" style={{
                fontFamily: "'DM Serif Display', serif",
                fontSize: "clamp(28px, 4vw, 42px)", fontWeight: "400",
                color: "var(--ink)", lineHeight: 1.2,
                letterSpacing: "-0.02em", marginBottom: "16px",
              }}>
                Ready when you are.
              </h2>
              <p style={{
                fontSize: "17px", color: "var(--ink-soft)",
                maxWidth: "440px", margin: "0 auto",
              }}>
                No signup. Free chats aren't saved. Just tell Irene what you're dealing with.
              </p>
            </div>
          </Reveal>
        )}

        {/* Chat container */}
        <Reveal delay={0.1}>
          <div style={{
            backgroundColor: "#fff",
            borderRadius: "20px",
            border: "1px solid var(--border)",
            overflow: "hidden",
            boxShadow: "0 4px 24px rgba(0,0,0,0.04)",
          }} role="region" aria-label="Chat with Irene">
            {/* Chat header — iMessage style */}
            <div style={{
              padding: "16px 20px",
              borderBottom: "1px solid var(--border-light)",
              display: "flex", alignItems: "center", gap: "12px",
              backgroundColor: "var(--surface)",
              justifyContent: "space-between",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <IreneAvatar />
                <div>
                  <span style={{
                    fontSize: "15px", fontWeight: "600", color: "var(--ink)",
                    display: "block", lineHeight: 1.3,
                  }}>Irene</span>
                  <span style={{
                    fontSize: "12px", color: isLoading ? "var(--warm)" : "var(--accent)",
                    fontWeight: "500",
                  }}>
                    {isLoading ? "writing a response..." : "AI medical bill assistant"}
                  </span>
                </div>
              </div>
              {/* History button — paid users only */}
              {isPaid && (
                <button
                  className="sidebar-toggle"
                  onClick={onToggleSidebar}
                  aria-label="Open conversation history"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path d="M7 1v6l4 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                  <span className="sidebar-label">History</span>
                </button>
              )}
              {/* Upsell for free signed-in users */}
              {user && !isPaid && (
                <button
                  onClick={onUpgrade}
                  style={{
                    fontSize: "12px", color: "var(--accent)", fontWeight: "500",
                    background: "none", border: "1px solid var(--accent-mist)",
                    borderRadius: "6px", padding: "5px 10px",
                    cursor: "pointer", fontFamily: "inherit",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => { e.target.style.backgroundColor = "var(--accent-mist)"; }}
                  onMouseLeave={(e) => { e.target.style.backgroundColor = "transparent"; }}
                >
                  Save chats →
                </button>
              )}
            </div>

            {!started ? (
              <div style={{ padding: "56px 32px", textAlign: "center", backgroundColor: "#fff" }}>
                <p style={{
                  fontSize: "16px", color: "var(--ink-soft)", marginBottom: "32px",
                  lineHeight: 1.7, maxWidth: "440px", margin: "0 auto 32px",
                }}>
                  I'm Irene, an AI assistant. I help families understand their medical bills, find financial
                  assistance, and figure out what to do next. Whatever you're facing — let's work through it.
                </p>
                <button onClick={handleStart} style={{
                  padding: "14px 32px", fontSize: "15px", fontWeight: "500",
                  backgroundColor: "var(--accent)", color: "#fff",
                  border: "none", borderRadius: "24px", cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                  onMouseEnter={(e) => { e.target.style.transform = "translateY(-1px)"; e.target.style.boxShadow = "0 4px 12px rgba(45,90,61,0.25)"; }}
                  onMouseLeave={(e) => { e.target.style.transform = "translateY(0)"; e.target.style.boxShadow = "none"; }}
                  aria-label="Start a conversation with Irene"
                >
                  Start a conversation
                </button>
              </div>
            ) : (
              <>
                <div
                  role="status"
                  aria-live="polite"
                  className="sr-only"
                >
                  {isLoading ? "Irene is writing a response" : ""}
                </div>
                <div
                  ref={messagesContainerRef}
                  style={{
                    height: "clamp(340px, 52vh, 480px)", overflowY: "auto",
                    padding: "20px 16px",
                    display: "flex", flexDirection: "column", gap: "12px",
                    backgroundColor: "#fff",
                  }}
                  role="log"
                  aria-live="polite"
                  aria-relevant="additions text"
                  aria-label="Conversation with Irene"
                >
                  {messages.map((msg, i) => (
                    <div key={i} style={{
                      display: "flex",
                      flexDirection: msg.role === "user" ? "row-reverse" : "row",
                      alignItems: "flex-end",
                      gap: "8px",
                    }}>
                      {msg.role === "assistant" && <IreneAvatar />}
                      <div
                        className={msg.role === "user" ? "imessage-bubble-user" : "imessage-bubble-irene"}
                        style={{
                          maxWidth: "75%", padding: "10px 16px",
                          fontSize: "15px", lineHeight: 1.6,
                          textAlign: "left",
                          whiteSpace: "pre-wrap",
                        }}
                        role={msg.role === "assistant" ? "status" : undefined}
                      >
                        {msg.role === "assistant" ? linkify(msg.content) : msg.content}
                      </div>
                    </div>
                  ))}
                  {/* Contextual upgrade prompt — after 4 messages for free users */}
                  {!isPaid && messages.length >= 4 && messages.length < 6 && (
                    <div style={{
                      padding: "12px 16px",
                      backgroundColor: "var(--accent-mist)",
                      borderRadius: "12px",
                      textAlign: "center",
                      margin: "4px 0",
                    }}>
                      <p style={{
                        fontSize: "13px", color: "var(--accent)",
                        lineHeight: 1.5, marginBottom: "8px",
                      }}>
                        Want to save this conversation and upload bills for analysis?
                      </p>
                      <button
                        onClick={onUpgrade}
                        style={{
                          fontSize: "13px", fontWeight: "600",
                          color: "#fff", backgroundColor: "var(--accent)",
                          border: "none", borderRadius: "16px",
                          padding: "6px 16px", cursor: "pointer",
                          fontFamily: "inherit", transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "var(--accent-light)"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "var(--accent)"}
                      >
                        Try Pro — $9/month
                      </button>
                    </div>
                  )}
                  {/* Second softer prompt after 8+ messages */}
                  {!isPaid && messages.length >= 8 && messages.length < 10 && (
                    <div style={{
                      textAlign: "center", padding: "8px 0",
                    }}>
                      <button
                        onClick={onUpgrade}
                        style={{
                          fontSize: "12px", color: "var(--ink-muted)",
                          background: "none", border: "none",
                          cursor: "pointer", fontFamily: "inherit",
                          textDecoration: "underline", textUnderlineOffset: "2px",
                        }}
                        onMouseEnter={(e) => e.target.style.color = "var(--accent)"}
                        onMouseLeave={(e) => e.target.style.color = "var(--ink-muted)"}
                      >
                        Upgrade to save conversations &amp; unlock bill analysis →
                      </button>
                    </div>
                  )}
                  {isLoading && (
                    <div style={{ display: "flex", alignItems: "flex-end", gap: "8px" }}>
                      <IreneAvatar />
                      <div className="imessage-bubble-irene" style={{ padding: "10px 16px" }}>
                        <ThinkingDots />
                      </div>
                    </div>
                  )}
                </div>

                {/* Input — iMessage style */}
                <div style={{
                  padding: "12px 16px", borderTop: "1px solid var(--border-light)",
                  backgroundColor: "var(--surface)",
                }}>
                  {showUpgradeHint && (
                    <div style={{
                      fontSize: "12px", color: "var(--ink-soft)",
                      marginBottom: "6px", paddingLeft: "4px",
                      display: "flex", alignItems: "center", gap: "6px",
                    }}>
                      <span>Bill upload is a Pro feature.</span>
                      <button onClick={onUpgrade} style={{
                        background: "none", border: "none", padding: 0,
                        fontSize: "12px", color: "var(--accent)", cursor: "pointer",
                        fontFamily: "inherit", textDecoration: "underline",
                        textUnderlineOffset: "2px",
                      }}>Upgrade →</button>
                    </div>
                  )}
                  <div style={{
                    display: "flex", gap: "8px", alignItems: "center",
                    backgroundColor: "#fff",
                    border: "1px solid var(--border)",
                    borderRadius: "24px",
                    padding: "4px 4px 4px 8px",
                  }}>
                    {/* Paperclip — shows upgrade prompt for free users */}
                    <button
                      onClick={() => { setShowUpgradeHint(true); setTimeout(() => setShowUpgradeHint(false), 4000); }}
                      aria-label="Upload a bill (Pro feature)"
                      title="Upload bill or EOB — Pro feature"
                      style={{
                        background: "none", border: "none", cursor: "pointer",
                        padding: "6px 6px 4px", color: "var(--ink-muted)",
                        display: "flex", alignItems: "center",
                        transition: "color 0.15s", flexShrink: 0, borderRadius: "50%",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = "var(--ink-soft)"}
                      onMouseLeave={(e) => e.currentTarget.style.color = "var(--ink-muted)"}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
                      </svg>
                    </button>
                    <label htmlFor="chat-input" style={{ position: "absolute", left: "-9999px" }}>
                      Type your message to Irene
                    </label>
                    <input
                      id="chat-input"
                      ref={inputRef}
                      type="text"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && input.trim() && !isLoading) {
                          sendMessage(input.trim());
                        }
                      }}
                      placeholder="Message Irene..."
                      disabled={isLoading}
                      aria-label="Type your message to Irene"
                      style={{
                        flex: 1, padding: "10px 0", fontSize: "15px",
                        border: "none", backgroundColor: "transparent",
                        color: "var(--ink)",
                        fontFamily: "inherit",
                      }}
                    />
                    <button
                      onClick={() => { if (input.trim() && !isLoading) sendMessage(input.trim()); }}
                      disabled={isLoading || !input.trim()}
                      aria-label="Send message"
                      style={{
                        width: "36px", height: "36px",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        backgroundColor: isLoading || !input.trim() ? "var(--border)" : "var(--accent)",
                        color: "#fff",
                        border: "none", borderRadius: "50%",
                        cursor: isLoading || !input.trim() ? "not-allowed" : "pointer",
                        transition: "all 0.2s ease",
                        fontSize: "16px", fontFamily: "inherit",
                        flexShrink: 0,
                      }}
                    >↑</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </Reveal>

        <p style={{
          fontSize: "12px", color: "var(--ink-faint)", marginTop: "16px",
          textAlign: "center",
        }} role="note">
          Irene provides general guidance only — not legal, financial, or medical advice.
          Always consult qualified professionals for your specific situation.
        </p>
      </div>
    </section>
  );
};

/* ─────────────────────── FAQ (AEO) ─────────────────────── */
const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      q: "Is Ask Irene really free?",
      a: "Yes. The chat advisor is completely free with no signup required. We believe every family deserves access to medical billing guidance, regardless of their financial situation."
    },
    {
      q: "Can Irene help me dispute a medical bill?",
      a: "Irene can walk you through the dispute process step by step — what to check for errors, who to call, what to say, and how to document everything. She can't make the calls for you, but she'll make sure you know exactly what to do."
    },
    {
      q: "Does Irene give legal or financial advice?",
      a: "No. Irene provides general educational guidance about medical billing, insurance, and patient rights. She's not a lawyer, financial advisor, or medical professional. Always consult qualified professionals for your specific situation."
    },
    {
      q: "What is hospital financial assistance and do I qualify?",
      a: "Most nonprofit hospitals are required by law (IRS Section 501(r)) to offer financial assistance programs. Many families qualify but never apply because they don't know these programs exist. Irene can help you figure out if you might qualify and how to apply."
    },
    {
      q: "Is my conversation with Irene private?",
      a: "Free conversations are not saved to an account — each session starts fresh. Pro subscribers can save conversation history for future reference, stored securely, visible only to you, and deletable anytime. Your messages are processed by our AI provider (Anthropic) to generate responses. We never sell your data, and it isn't used to train AI models."
    },
    {
      q: "What's an EOB and why does it matter?",
      a: "An Explanation of Benefits (EOB) is a statement from your insurance company showing what they paid and what you owe. EOBs often contain errors — wrong codes, duplicate charges, or services billed at incorrect rates. Irene can help you understand what to look for."
    },
  ];

  return (
    <section style={{
      padding: "clamp(64px, 10vh, 120px) 24px",
      maxWidth: "1200px", margin: "0 auto",
    }} aria-labelledby="faq-heading">
      <Reveal>
        <h2 id="faq-heading" style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: "clamp(28px, 4vw, 42px)", fontWeight: "400",
          color: "var(--ink)", lineHeight: 1.2,
          letterSpacing: "-0.02em", marginBottom: "48px",
        }}>
          Common questions.
        </h2>
      </Reveal>

      <div style={{ maxWidth: "720px" }}>
        {faqs.map((faq, i) => (
          <Reveal key={i} delay={i * 0.05}>
            <div style={{ borderBottom: "1px solid var(--border-light)" }}>
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                style={{
                  width: "100%", padding: "20px 0",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  background: "none", border: "none", cursor: "pointer",
                  fontFamily: "inherit", textAlign: "left",
                }}
                aria-expanded={openIndex === i}
                aria-controls={`faq-answer-${i}`}
              >
                <span style={{
                  fontSize: "16px", fontWeight: "500", color: "var(--ink)",
                  paddingRight: "24px",
                }}>{faq.q}</span>
                <span style={{
                  fontSize: "20px", color: "var(--ink-muted)",
                  transform: openIndex === i ? "rotate(45deg)" : "none",
                  transition: "transform 0.3s ease",
                  flexShrink: 0,
                }}>+</span>
              </button>
              <div
                id={`faq-answer-${i}`}
                role="region"
                style={{
                  maxHeight: openIndex === i ? "200px" : "0",
                  overflow: "hidden",
                  transition: "max-height 0.4s ease",
                }}
              >
                <p style={{
                  fontSize: "15px", lineHeight: 1.7, color: "var(--ink-soft)",
                  paddingBottom: "20px", maxWidth: "600px",
                }}>{faq.a}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

/* ─────────────────────── EMAIL CAPTURE ─────────────────────── */
const EmailCapture = () => {
  const [email, setEmail] = useState("");
  const [botField, setBotField] = useState(""); // honeypot: hidden from people, bots fill it
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!email.includes("@") || !email.includes(".")) {
      setError("Please enter a valid email address.");
      return;
    }
    setError("");
    try {
      // Saved to the email_signups table by netlify/functions/subscribe.mjs
      const response = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, "bot-field": botField }),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.error || "Something went wrong. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    }
  };

  return (
    <section style={{
      padding: "clamp(64px, 10vh, 100px) 24px",
      backgroundColor: "var(--surface-warm)",
      borderTop: "1px solid var(--border-light)",
    }} aria-labelledby="guide-heading">
      <div style={{ maxWidth: "560px", margin: "0 auto", textAlign: "center" }}>
        <Reveal>
          <h2 id="guide-heading" style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: "clamp(24px, 3.5vw, 36px)", fontWeight: "400",
            color: "var(--ink)", lineHeight: 1.2,
            letterSpacing: "-0.02em", marginBottom: "16px",
          }}>
            The Cancer Cost Survival Guide
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p style={{
            fontSize: "16px", color: "var(--ink-soft)", marginBottom: "32px",
            lineHeight: 1.7,
          }}>
            Everything we wish we'd known from day one. What to check on every bill,
            how to apply for financial assistance, and the rights you didn't know you had.
            Written by a family that's been through it.
          </p>
        </Reveal>

        <Reveal delay={0.2}>
          {!submitted ? (
            <div>
              <div style={{
                display: "flex", gap: "8px", maxWidth: "420px", margin: "0 auto",
                flexWrap: "wrap", justifyContent: "center",
              }}>
                <label htmlFor="email-capture-input" style={{ position: "absolute", left: "-9999px" }}>
                  Email address
                </label>
                {/* Honeypot field: off-screen and skipped by keyboard and screen readers */}
                <input
                  type="text"
                  name="bot-field"
                  value={botField}
                  onChange={(e) => setBotField(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px" }}
                />
                <input
                  id="email-capture-input"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
                  placeholder="Your email address"
                  aria-label="Email address for the Cancer Cost Survival Guide"
                  style={{
                    flex: "1 1 220px", padding: "14px 16px", fontSize: "14px",
                    border: `1px solid ${error ? "#c44" : "var(--border)"}`,
                    borderRadius: "6px", backgroundColor: "var(--surface)",
                    color: "var(--ink)",
                    transition: "border-color 0.2s", fontFamily: "inherit",
                  }}
                  onFocus={(e) => e.target.style.borderColor = "var(--accent)"}
                  onBlur={(e) => e.target.style.borderColor = error ? "#c44" : "var(--border)"}
                />
                <button
                  onClick={handleSubmit}
                  style={{
                    padding: "14px 24px", fontSize: "14px", fontWeight: "500",
                    backgroundColor: "var(--ink)", color: "var(--surface)",
                    border: "none", borderRadius: "6px", cursor: "pointer",
                    transition: "all 0.2s ease", whiteSpace: "nowrap",
                    fontFamily: "inherit",
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = "var(--accent)"}
                  onMouseLeave={(e) => e.target.style.backgroundColor = "var(--ink)"}
                  aria-label="Submit email to receive the guide"
                >
                  Get the free guide
                </button>
              </div>
              {error && (
                <p style={{
                  fontSize: "13px", color: "#c44", marginTop: "8px",
                }} role="alert">{error}</p>
              )}
            </div>
          ) : (
            <div style={{ padding: "20px 0" }}>
              <p style={{
                fontSize: "16px", color: "var(--accent)", fontWeight: "500",
                marginBottom: "16px",
              }} role="status">
                Thank you. Here's your guide:
              </p>
              <a
                href="/cancer-cost-survival-guide.pdf"
                download
                style={{
                  display: "inline-block",
                  padding: "14px 28px", fontSize: "14px", fontWeight: "500",
                  backgroundColor: "var(--accent)", color: "#fff",
                  border: "none", borderRadius: "6px", cursor: "pointer",
                  textDecoration: "none",
                  transition: "all 0.2s ease",
                  maxWidth: "340px",
                  width: "auto",
                }}
                onMouseEnter={(e) => { e.target.style.transform = "translateY(-1px)"; e.target.style.boxShadow = "0 4px 12px rgba(45,90,61,0.25)"; }}
                onMouseLeave={(e) => { e.target.style.transform = "translateY(0)"; e.target.style.boxShadow = "none"; }}
              >
                Download the Guide (PDF)
              </a>
            </div>
          )}
        </Reveal>

        <p style={{
          fontSize: "12px", color: "var(--ink-faint)", marginTop: "16px",
        }}>
          We may occasionally email you helpful updates. No spam. Unsubscribe anytime.
        </p>
      </div>
    </section>
  );
};

/* ─────────────────────── FOOTER ─────────────────────── */
const Footer = ({ onPrivacy, onTerms }) => (
  <footer style={{
    padding: "32px 24px",
    borderTop: "1px solid var(--border-light)",
  }} role="contentinfo">
    <div style={{
      maxWidth: "1200px", margin: "0 auto",
      display: "flex", justifyContent: "space-between", alignItems: "center",
      flexWrap: "wrap", gap: "16px",
    }}>
      <span style={{
        fontFamily: "'DM Serif Display', serif",
        fontSize: "16px", color: "var(--ink)",
      }}>Ask Irene</span>
      <div style={{
        display: "flex", alignItems: "center", gap: "24px", flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", gap: "16px" }}>
          <button onClick={onPrivacy} style={{
            fontSize: "12px", color: "var(--ink-muted)", background: "none",
            border: "none", cursor: "pointer", fontFamily: "inherit",
            textDecoration: "underline", textUnderlineOffset: "2px",
          }}>Privacy</button>
          <button onClick={onTerms} style={{
            fontSize: "12px", color: "var(--ink-muted)", background: "none",
            border: "none", cursor: "pointer", fontFamily: "inherit",
            textDecoration: "underline", textUnderlineOffset: "2px",
          }}>Terms</button>
          <span style={{ fontSize: "12px", color: "var(--ink-muted)" }}>
            <CookieSettingsButton />
          </span>
        </div>
        <p style={{
          fontSize: "12px", color: "var(--ink-muted)",
          maxWidth: "400px",
        }}>
          Ask Irene provides general information, not professional advice.
          Always consult qualified professionals for your specific situation.
        </p>
      </div>
    </div>
  </footer>
);

/* ─────────────────────── APP ─────────────────────── */

// Map view names to URL paths
const viewToPath = {
  home: "/",
  landing: "/",
  account: "/account",
  upgrade: "/upgrade",
  privacy: "/privacy",
  terms: "/terms",
  about: "/about",
  compare: "/pricing",
  blog: "/blog",
};

const pathToView = {
  "/": "home",
  "/account": "account",
  "/upgrade": "upgrade",
  "/privacy": "privacy",
  "/terms": "terms",
  "/about": "about",
  "/pricing": "compare",
  "/blog": "blog",
};

function getInitialView() {
  const path = window.location.pathname;
  if (path === "/blog") return "blog";
  if (path.startsWith("/blog/")) return "blog-post";
  return pathToView[path] || "home";
}

export default function AskIrene() {
  const { user, profile, signInWithGoogle } = useAuth();
  const [view, setViewState] = useState(getInitialView);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentPostSlug, setCurrentPostSlug] = useState(() => {
    const path = window.location.pathname;
    return path.startsWith("/blog/") ? path.slice(6) : null;
  });

  const isPaid = profile?.tier === "paid";
  const conversationHook = useConversations();

  // Navigate: update view state + URL path + scroll to top
  const navigate = useCallback((newView, overridePath) => {
    setViewState(newView);
    const path = overridePath || viewToPath[newView] || "/";
    if (window.location.pathname !== path) {
      window.history.pushState({ view: newView }, "", path);
    }
    window.scrollTo(0, 0);
  }, []);

  const navigateToPost = useCallback((slug) => {
    setCurrentPostSlug(slug);
    navigate("blog-post", `/blog/${slug}`);
  }, [navigate]);

  // Alias for compatibility — used the same way as setView was
  const setView = navigate;

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      if (path === "/blog") {
        setViewState("blog");
      } else if (path.startsWith("/blog/")) {
        setCurrentPostSlug(path.slice(6));
        setViewState("blog-post");
      } else {
        setViewState(pathToView[path] || "home");
      }
      window.scrollTo(0, 0);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Load conversation list when a paid user signs in
  const { loadConversations } = conversationHook;
  useEffect(() => {
    if (isPaid && user) {
      loadConversations();
    }
  }, [isPaid, user, loadConversations]);

  // Handle Stripe redirect ?upgrade=success
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("upgrade") === "success") {
      setViewState("account");
      window.history.replaceState(null, "", "/account");
    }
  }, []);

  const scrollTo = (id) => {
    // If on landing page, just scroll
    if (view === "landing" || view === "home") {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    // Navigate home first, then scroll
    setViewState("home");
    window.history.pushState({ view: "home" }, "", "/");
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const handleSelectConversation = async (conversationId) => {
    conversationHook.setActiveConversationId(conversationId);
    if (view === "landing") setView("home");
  };

  const handleNewConversation = () => {
    if (conversationHook._handleNewConversation) {
      conversationHook._handleNewConversation();
    }
    conversationHook.setActiveConversationId(null);
    if (view === "landing") setView("home");
  };

  const renderNav = () => (
    <Nav
      onScrollTo={scrollTo}
      user={user}
      isPaid={isPaid}
      onSignIn={signInWithGoogle}
      onAccount={() => setView("account")}
      onAbout={() => setView("about")}
      onCompare={() => setView("compare")}
      onBlog={() => setView("blog")}
      onBackToChat={isPaid ? () => setView("home") : null}
      onHome={user ? () => setView("landing") : null}
    />
  );

  // Sidebar — only rendered for paid users
  const renderSidebar = () => {
    if (!isPaid) return null;
    return (
      <ConversationSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        conversations={conversationHook.conversations}
        activeConversationId={conversationHook.activeConversationId}
        onSelectConversation={handleSelectConversation}
        onNewConversation={handleNewConversation}
        onDeleteConversation={conversationHook.deleteConversation}
        loading={conversationHook.loadingHistory}
      />
    );
  };

  // ── PAID USER: Full-page chat experience ──
  if (isPaid && view !== "account" && view !== "upgrade" && view !== "landing" && view !== "privacy" && view !== "terms" && view !== "about" && view !== "compare" && view !== "blog" && view !== "blog-post") {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "var(--surface)" }}>
        <GlobalStyles />
        {renderSidebar()}
        <a href="#main" className="skip-link">Skip to main content</a>
        <FullPageChat
          user={user}
          conversationHook={conversationHook}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          onAccount={() => setView("account")}
          onHome={() => setView("landing")}
          onAbout={() => setView("about")}
          onCompare={() => setView("compare")}
          onBlog={() => setView("blog")}
        />
      </div>
    );
  }

  // ── Account page ──
  if (view === "account") {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "var(--surface)" }}>
        <GlobalStyles />
        {renderSidebar()}
        <a href="#main" className="skip-link">Skip to main content</a>
        {renderNav()}
        <main id="main">
          <Account
            onBack={() => setView(isPaid ? "home" : "home")}
            onUpgrade={() => setView("upgrade")}
            onDeleteAllConversations={conversationHook.deleteAllConversations}
          />
        </main>
        <Footer onPrivacy={() => setView("privacy")} onTerms={() => setView("terms")} />
      </div>
    );
  }

  // ── Upgrade page ──
  if (view === "upgrade") {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "var(--surface)" }}>
        <GlobalStyles />
        {renderSidebar()}
        <a href="#main" className="skip-link">Skip to main content</a>
        {renderNav()}
        <main id="main">
          <Upgrade onBack={() => setView(isPaid ? "home" : "home")} />
        </main>
        <Footer onPrivacy={() => setView("privacy")} onTerms={() => setView("terms")} />
      </div>
    );
  }

  // ── Privacy Policy ──
  if (view === "privacy") {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "var(--surface)" }}>
        <GlobalStyles />
        <a href="#main" className="skip-link">Skip to main content</a>
        {renderNav()}
        <main id="main">
          <PrivacyPolicy onBack={() => setView(isPaid ? "home" : "home")} />
        </main>
        <Footer onPrivacy={() => setView("privacy")} onTerms={() => setView("terms")} />
      </div>
    );
  }

  // ── Terms of Service ──
  if (view === "terms") {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "var(--surface)" }}>
        <GlobalStyles />
        <a href="#main" className="skip-link">Skip to main content</a>
        {renderNav()}
        <main id="main">
          <TermsOfService onBack={() => setView(isPaid ? "home" : "home")} />
        </main>
        <Footer onPrivacy={() => setView("privacy")} onTerms={() => setView("terms")} />
      </div>
    );
  }

  // ── About page ──
  if (view === "about") {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "var(--surface)" }}>
        <GlobalStyles />
        <a href="#main" className="skip-link">Skip to main content</a>
        {renderNav()}
        <main id="main">
          <About onBack={() => setView(isPaid ? "home" : "home")} />
        </main>
        <Footer onPrivacy={() => setView("privacy")} onTerms={() => setView("terms")} />
      </div>
    );
  }

  // ── Blog index ──
  if (view === "blog") {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "var(--surface)" }}>
        <GlobalStyles />
        {renderSidebar()}
        <a href="#main" className="skip-link">Skip to main content</a>
        {renderNav()}
        <main id="main">
          <BlogIndex
            onBack={() => setView(isPaid ? "home" : "home")}
            onPostSelect={navigateToPost}
          />
        </main>
        <Footer onPrivacy={() => setView("privacy")} onTerms={() => setView("terms")} />
      </div>
    );
  }

  // ── Blog post ──
  if (view === "blog-post") {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "var(--surface)" }}>
        <GlobalStyles />
        {renderSidebar()}
        <a href="#main" className="skip-link">Skip to main content</a>
        {renderNav()}
        <main id="main">
          <BlogPost
            slug={currentPostSlug}
            onBlogIndex={() => setView("blog")}
            onBack={() => setView("blog")}
            onStartChat={() => setView(isPaid ? "home" : "home")}
          />
        </main>
        <Footer onPrivacy={() => setView("privacy")} onTerms={() => setView("terms")} />
      </div>
    );
  }

  // ── Compare plans ──
  if (view === "compare") {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "var(--surface)" }}>
        <GlobalStyles />
        <a href="#main" className="skip-link">Skip to main content</a>
        {renderNav()}
        <main id="main">
          <ComparePlans
            onBack={() => setView(isPaid ? "home" : "home")}
            onUpgrade={() => setView("upgrade")}
            isPaid={isPaid}
            user={user}
          />
        </main>
        <Footer onPrivacy={() => setView("privacy")} onTerms={() => setView("terms")} />
      </div>
    );
  }

  // ── Landing page (for free/anonymous users, or paid users who clicked "View site") ──
  return (
    <div style={{ minHeight: "100vh", backgroundColor: "var(--surface)" }}>
      <GlobalStyles />
      {renderSidebar()}
      <a href="#main" className="skip-link">Skip to main content</a>
      {renderNav()}
      <main id="main">
        <Hero onScrollTo={scrollTo} />
        <ChatAdvisor
          isPaid={isPaid}
          user={user}
          conversationHook={isPaid ? conversationHook : null}
          onUpgrade={() => setView("upgrade")}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        />
        <Divider />
        <HowItWorks />
        <Context />
        <Capabilities />
        <Divider />
        <FAQ />
        <EmailCapture />
      </main>
      <Footer onPrivacy={() => setView("privacy")} onTerms={() => setView("terms")} />
    </div>
  );
}
